import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Box,
  Paper,
  Chip,
  CardHeader,
  Avatar,
  useTheme,
  alpha,
  InputAdornment,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Settings as SettingsIcon,
  PhoneAndroid,
  Apple,
  Update,
  Build,
  Info,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Save
} from '@mui/icons-material';
import api from '../api';

const Settings = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState({
    isMaintenance: false,
    maintenanceMessage: '',
    android: {
      isForceUpdate: false,
      appVersion: '',
      minVersion: '',
      updateMessage: '',
      playStoreUrl: ''
    },
    ios: {
      isForceUpdate: false,
      appVersion: '',
      minVersion: '',
      updateMessage: '',
      appStoreUrl: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings');
      if (response.data.status) {
        const fetchedSettings = response.data.data.settings;
        // Ensure android and ios objects exist with defaults
        setSettings({
          isMaintenance: fetchedSettings.isMaintenance || false,
          maintenanceMessage: fetchedSettings.maintenanceMessage || '',
          android: {
            isForceUpdate: fetchedSettings.android?.isForceUpdate || false,
            appVersion: fetchedSettings.android?.appVersion || '',
            minVersion: fetchedSettings.android?.minVersion || '',
            updateMessage: fetchedSettings.android?.updateMessage || '',
            playStoreUrl: fetchedSettings.android?.playStoreUrl || ''
          },
          ios: {
            isForceUpdate: fetchedSettings.ios?.isForceUpdate || false,
            appVersion: fetchedSettings.ios?.appVersion || '',
            minVersion: fetchedSettings.ios?.minVersion || '',
            updateMessage: fetchedSettings.ios?.updateMessage || '',
            appStoreUrl: fetchedSettings.ios?.appStoreUrl || ''
          }
        });
      } else {
        showAlert('error', 'Failed to load settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showAlert('error', 'Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePlatformChange = (platform, field, value) => {
    setSettings(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  const handleSaveGlobal = async () => {
    try {
      setSaving(true);
      const response = await api.post('/admin/settings', {
        isMaintenance: settings.isMaintenance,
        maintenanceMessage: settings.maintenanceMessage
      });
      if (response.data.status) {
        showAlert('success', 'Global settings updated successfully');
        fetchSettings(); // Refresh to get updated data
      } else {
        showAlert('error', 'Failed to update global settings');
      }
    } catch (error) {
      console.error('Error saving global settings:', error);
      showAlert('error', 'Error saving global settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlatform = async (platform) => {
    try {
      setSaving(true);
      const response = await api.post('/admin/settings', {
        [platform]: settings[platform]
      });
      if (response.data.status) {
        showAlert('success', `${platform.toUpperCase()} settings updated successfully`);
        fetchSettings(); // Refresh to get updated data
      } else {
        showAlert('error', `Failed to update ${platform} settings`);
      }
    } catch (error) {
      console.error(`Error saving ${platform} settings:`, error);
      showAlert('error', `Error saving ${platform} settings`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      const response = await api.post('/admin/settings', settings);
      if (response.data.status) {
        showAlert('success', 'All settings updated successfully');
        fetchSettings(); // Refresh to get updated data
      } else {
        showAlert('error', 'Failed to update settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showAlert('error', 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ 
            bgcolor: theme.palette.primary.main, 
            mr: 2,
            width: 56,
            height: 56
          }}>
            <SettingsIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              App Settings
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Platform-specific version control for Android and iOS
            </Typography>
          </Box>
        </Box>
        
        {/* Status Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={settings.isMaintenance ? <Warning /> : <CheckCircle />}
            label={settings.isMaintenance ? 'Maintenance Mode' : 'App Online'}
            color={settings.isMaintenance ? 'warning' : 'success'}
            variant="outlined"
          />
          <Chip
            icon={<PhoneAndroid />}
            label={`Android v${settings.android?.appVersion || '1.0.0'}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            icon={<Apple />}
            label={`iOS v${settings.ios?.appVersion || '1.0.0'}`}
            color="secondary"
            variant="outlined"
          />
        </Box>
      </Box>
      
      {alert.show && (
        <Alert 
          severity={alert.type} 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: '1.5rem' }
          }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Platform-Specific Settings Card with Tabs */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3,
        boxShadow: theme.shadows[8]
      }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <Update />
            </Avatar>
          }
          title="Platform-Specific Version Control"
          subheader="Manage Android and iOS app versions independently"
          sx={{ pb: 0 }}
        />
        <CardContent>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab 
              icon={<PhoneAndroid />} 
              iconPosition="start" 
              label="Android Settings" 
              sx={{ fontWeight: 600 }}
            />
            <Tab 
              icon={<Apple />} 
              iconPosition="start" 
              label="iOS Settings" 
              sx={{ fontWeight: 600 }}
            />
          </Tabs>

          {/* Android Settings Tab */}
          {tabValue === 0 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneAndroid /> Android Version Management
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="App Version"
                    value={settings.android.appVersion}
                    onChange={(e) => handlePlatformChange('android', 'appVersion', e.target.value)}
                    helperText="Latest Android app version (e.g., 2.5.0)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Update sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Minimum Version"
                    value={settings.android.minVersion}
                    onChange={(e) => handlePlatformChange('android', 'minVersion', e.target.value)}
                    helperText="Minimum required Android version"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Warning sx={{ color: 'warning.main' }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.android.isForceUpdate}
                        onChange={(e) => handlePlatformChange('android', 'isForceUpdate', e.target.checked)}
                        color="error"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Force Update for Android
                        </Typography>
                        {settings.android.isForceUpdate && (
                          <Chip label="ACTIVE" color="error" size="small" />
                        )}
                      </Box>
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Update Message"
                    value={settings.android.updateMessage}
                    onChange={(e) => handlePlatformChange('android', 'updateMessage', e.target.value)}
                    helperText="Message shown to Android users when update is required"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Play Store URL"
                    value={settings.android.playStoreUrl}
                    onChange={(e) => handlePlatformChange('android', 'playStoreUrl', e.target.value)}
                    helperText="Google Play Store download link"
                    placeholder="https://play.google.com/store/apps/details?id=..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneAndroid sx={{ color: 'success.main' }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => handleSavePlatform('android')}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    sx={{ px: 4 }}
                  >
                    {saving ? 'Saving...' : 'Save Android Settings'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* iOS Settings Tab */}
          {tabValue === 1 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" color="secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Apple /> iOS Version Management
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="App Version"
                    value={settings.ios.appVersion}
                    onChange={(e) => handlePlatformChange('ios', 'appVersion', e.target.value)}
                    helperText="Latest iOS app version (e.g., 2.6.0)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Update sx={{ color: 'secondary.main' }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Minimum Version"
                    value={settings.ios.minVersion}
                    onChange={(e) => handlePlatformChange('ios', 'minVersion', e.target.value)}
                    helperText="Minimum required iOS version"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Warning sx={{ color: 'warning.main' }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.ios.isForceUpdate}
                        onChange={(e) => handlePlatformChange('ios', 'isForceUpdate', e.target.checked)}
                        color="error"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Force Update for iOS
                        </Typography>
                        {settings.ios.isForceUpdate && (
                          <Chip label="ACTIVE" color="error" size="small" />
                        )}
                      </Box>
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Update Message"
                    value={settings.ios.updateMessage}
                    onChange={(e) => handlePlatformChange('ios', 'updateMessage', e.target.value)}
                    helperText="Message shown to iOS users when update is required"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="App Store URL"
                    value={settings.ios.appStoreUrl}
                    onChange={(e) => handlePlatformChange('ios', 'appStoreUrl', e.target.value)}
                    helperText="Apple App Store download link"
                    placeholder="https://apps.apple.com/app/id..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Apple sx={{ color: 'info.main' }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={() => handleSavePlatform('ios')}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    sx={{ px: 4 }}
                  >
                    {saving ? 'Saving...' : 'Save iOS Settings'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Maintenance Mode Card */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3,
        boxShadow: theme.shadows[8],
        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.02)} 0%, ${alpha(theme.palette.warning.main, 0.08)} 100%)`
      }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
              <Build />
            </Avatar>
          }
          title="Maintenance Mode"
          subheader="Control app availability and maintenance messaging"
          sx={{ pb: 1 }}
        />
        <CardContent>
          <Grid container spacing={3}>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.isMaintenance}
                    onChange={(e) => handleGlobalChange('isMaintenance', e.target.checked)}
                    color="warning"
                    size="large"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Warning sx={{ mr: 1, color: settings.isMaintenance ? 'warning.main' : 'action.disabled' }} />
                    <Typography variant="body1">
                      {settings.isMaintenance ? 'Maintenance Active' : 'Maintenance Inactive'}
                    </Typography>
                  </Box>
                }
                sx={{ 
                  '& .MuiFormControlLabel-label': { 
                    fontSize: '1rem', 
                    fontWeight: 500 
                  } 
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Maintenance Message"
                value={settings.maintenanceMessage}
                onChange={(e) => handleGlobalChange('maintenanceMessage', e.target.value)}
                helperText="Message shown to users during maintenance"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                color="warning"
                size="large"
                onClick={handleSaveGlobal}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                sx={{ px: 4 }}
              >
                {saving ? 'Saving...' : 'Save Global Settings'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Save All Button */}
      <Paper sx={{ 
        p: 3, 
        borderRadius: 3,
        background: alpha(theme.palette.primary.main, 0.04),
        border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Save All Settings at Once
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update Android, iOS, and global settings together
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSaveAll}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            sx={{ 
              px: 4, 
              py: 1.5,
              borderRadius: 2,
              boxShadow: theme.shadows[4],
              '&:hover': {
                boxShadow: theme.shadows[8]
              }
            }}
          >
            {saving ? 'Saving All...' : 'Save All Settings'}
          </Button>
        </Box>
      </Paper>

      {/* Current Settings Summary */}
      <Card sx={{ 
        mt: 3, 
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.02)} 0%, ${alpha(theme.palette.info.main, 0.08)} 100%)`,
        border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
      }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: theme.palette.info.main }}>
              <Info />
            </Avatar>
          }
          title="Configuration Summary"
          subheader="Current platform-specific settings overview"
        />
        <CardContent>
          <Grid container spacing={3}>
            {/* Android Summary */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default', border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
                <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneAndroid /> Android
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Version:</Typography>
                  <Chip label={settings.android?.appVersion || 'Not set'} size="small" color="primary" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Min Version:</Typography>
                  <Chip label={settings.android?.minVersion || 'Not set'} size="small" color="default" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Force Update:</Typography>
                  <Chip 
                    label={settings.android?.isForceUpdate ? 'ON' : 'OFF'} 
                    size="small" 
                    color={settings.android?.isForceUpdate ? 'error' : 'success'} 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Play Store:</Typography>
                  <Chip 
                    label={settings.android?.playStoreUrl ? 'Set' : 'Not set'} 
                    size="small" 
                    color={settings.android?.playStoreUrl ? 'success' : 'default'} 
                  />
                </Box>
              </Paper>
            </Grid>

            {/* iOS Summary */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default', border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}` }}>
                <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Apple /> iOS
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Version:</Typography>
                  <Chip label={settings.ios?.appVersion || 'Not set'} size="small" color="secondary" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Min Version:</Typography>
                  <Chip label={settings.ios?.minVersion || 'Not set'} size="small" color="default" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Force Update:</Typography>
                  <Chip 
                    label={settings.ios?.isForceUpdate ? 'ON' : 'OFF'} 
                    size="small" 
                    color={settings.ios?.isForceUpdate ? 'error' : 'success'} 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">App Store:</Typography>
                  <Chip 
                    label={settings.ios?.appStoreUrl ? 'Set' : 'Not set'} 
                    size="small" 
                    color={settings.ios?.appStoreUrl ? 'success' : 'default'} 
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Global Summary */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default', border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}` }}>
                <Typography variant="subtitle2" color="warning.main" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Build /> Global Settings
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Maintenance:</Typography>
                  <Chip 
                    label={settings.isMaintenance ? 'Active' : 'Inactive'} 
                    size="small" 
                    color={settings.isMaintenance ? 'warning' : 'success'} 
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Maintenance Message:
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontSize: '0.75rem',
                    maxHeight: '60px',
                    overflow: 'auto',
                    p: 1,
                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                    borderRadius: 1
                  }}>
                    {settings.maintenanceMessage || 'Not set'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Settings;